import React, { useState, useEffect, useMemo } from 'react';
import { graphql } from 'react-relay';
import { defineMessages, useIntl } from 'react-intl';
import noop from 'lodash/noop';

import { cssMap } from '@atlaskit/css';
import Select from '@atlaskit/select';
import Spinner from '@atlaskit/spinner';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import StatusWorkflowInReviewIcon from '@atlaskit/icon-lab/core/status-workflow-in-review';
import StatusWorkflowReturnedIcon from '@atlaskit/icon-lab/core/status-workflow-returned';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { type FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { DisableApprovalsModal } from '@confluence/approvals/entry-points/DisableApprovalsModal.ts';

import { PRIMARY_COLORS } from '../colors.ts';

import type {
	StatusAndApprovalsConfigSettingsQuery$data,
	StatusAndApprovalsConfigSettingsQuery$variables,
} from './__generated__/StatusAndApprovalsConfigSettingsQuery.graphql.ts';
import type {
	StatusAndApprovalsConfigApprovalStatusesQuery$data,
	StatusAndApprovalsConfigApprovalStatusesQuery$variables,
} from './__generated__/StatusAndApprovalsConfigApprovalStatusesQuery.graphql.ts';
import type {
	StatusAndApprovalsConfigSetContentStateSettingsMutation$data,
	StatusAndApprovalsConfigSetContentStateSettingsMutation$variables,
} from './__generated__/StatusAndApprovalsConfigSetContentStateSettingsMutation.graphql.ts';
import type {
	StatusAndApprovalsConfigSetApprovalsMutation$data,
	StatusAndApprovalsConfigSetApprovalsMutation$variables,
} from './__generated__/StatusAndApprovalsConfigSetApprovalsMutation.graphql.ts';
import { i18n as sharedI18n } from './i18n.ts';
import { ToggleSection } from './ToggleSection.tsx';
import { SuggestedContentStateCustomizationArea } from './SuggestedContentStateCustomizationArea.tsx';
import { SuggestedContentState } from './SuggestedContentState.tsx';
import type { AdditionalRow, ContentState } from './types.ts';

const i18n = defineMessages({
	disabled: {
		id: 'content-state.space-admin.status-and-approvals.mode.disabled',
		defaultMessage: 'Disabled',
		description: 'Select option to disable content statuses and approvals',
	},
	statusesOnly: {
		id: 'content-state.space-admin.status-and-approvals.mode.statuses-only',
		defaultMessage: 'Enable statuses only',
		description: 'Select option to enable content statuses only',
	},
	statusesWithApprovals: {
		id: 'content-state.space-admin.status-and-approvals.mode.statuses-with-approvals',
		defaultMessage: 'Enable statuses with approvals',
		description: 'Select option to enable content statuses and approvals workflow',
	},
	startApproval: {
		id: 'content-state.space-admin.status-and-approvals.start-approval',
		defaultMessage: 'Start approval',
		description: 'Label shown next to workflow statuses that allow initiating an approval',
	},
	suggestedStatesRequiredForApprovals: {
		id: 'content-state.space-admin.status-and-approvals.suggested-states-required',
		defaultMessage: 'Suggested statuses are required when approvals are enabled',
		description:
			'Tooltip shown on the disabled Suggest statuses toggle when approvals mode is active',
	},
	errorFlagTitle: {
		id: 'content-state.space-admin.status-and-approvals.error-flag.title',
		defaultMessage: 'Something went wrong',
		description: 'Title for error flag shown when a settings change fails',
	},
	errorFlagDescription: {
		id: 'content-state.space-admin.status-and-approvals.error-flag.description',
		defaultMessage: 'Your changes could not be saved. Please try again.',
		description: 'Description for error flag shown when a settings change fails',
	},
});

const spinnerStyles = cssMap({
	root: {
		display: 'flex',
		justifyContent: 'center',
	},
});

const selectWrapperStyles = cssMap({
	root: {
		maxWidth: '600px',
	},
});

const statusesSectionStyles = cssMap({
	root: {
		paddingBlockStart: token('space.200'),
	},
});

type Mode = 'disabled' | 'statuses' | 'approvals';

export const IN_REVIEW_STATUS_ID = '1';
export const APPROVED_STATUS_ID = '2';
export const CHANGES_REQUESTED_STATUS_ID = '3';

type StatusAndApprovalsConfigProps = {
	spaceKey: string;
	flags: FlagsStateContainer;
};

type SettingsState = {
	mode: Mode;
	suggestedEnabled: boolean;
	customEnabled: boolean;
	suggestedStatesArray: Array<ContentState | null>;
};

const settingsQuery = graphql`
	query StatusAndApprovalsConfigSettingsQuery($spaceKey: String!) {
		space(key: $spaceKey) {
			id
			settings {
				contentStateSettings {
					contentStatesAllowed
					spaceContentStatesAllowed
					customContentStatesAllowed
					spaceContentStates {
						name
						color
						id
						restrictionLevel
					}
				}
			}
			spaceTypeSettings {
				enabledFeatures {
					isContentApprovalsEnabled
				}
			}
		}
	}
`;

const approvalStatusesQuery = graphql`
	query StatusAndApprovalsConfigApprovalStatusesQuery($cloudId: ID!) {
		confluence_contentApprovalStatuses(cloudId: $cloudId, transitionType: null) {
			allowsReviews
			id
			displayProperties {
				name
			}
		}
	}
`;

const setContentStateMutation = graphql`
	mutation StatusAndApprovalsConfigSetContentStateSettingsMutation(
		$spaceKey: String!
		$contentStatesSetting: Boolean!
		$customStatesSetting: Boolean!
		$spaceStatesSetting: Boolean!
	) {
		setContentStateSettings(
			spaceKey: $spaceKey
			contentStatesSetting: $contentStatesSetting
			customStatesSetting: $customStatesSetting
			spaceStatesSetting: $spaceStatesSetting
		) {
			contentStatesAllowed
			spaceContentStatesAllowed
			customContentStatesAllowed
			spaceContentStates {
				name
				color
				id
				restrictionLevel
			}
		}
	}
`;

const setApprovalsMutation = graphql`
	mutation StatusAndApprovalsConfigSetApprovalsMutation(
		$cloudId: ID!
		$spaceKey: String!
		$enabled: Boolean!
	) {
		confluence_setContentApprovalsSpaceSettings(
			cloudId: $cloudId
			spaceKey: $spaceKey
			enabled: $enabled
		) {
			success
			isContentApprovalsEnabled
			errors {
				message
				extensions {
					errorType
				}
			}
		}
	}
`;

const StatusAndApprovalsConfigComponent = ({ spaceKey, flags }: StatusAndApprovalsConfigProps) => {
	const { cloudId } = useSessionData();
	const { formatMessage } = useIntl();

	const showErrorFlag = (error: Error) => {
		markErrorAsHandled(error);
		void flags.showErrorFlag({
			id: 'status-and-approvals-config-error',
			title: formatMessage(i18n.errorFlagTitle),
			description: formatMessage(i18n.errorFlagDescription),
		});
	};

	const originalSuggestedStatesArray = useMemo<ContentState[]>(
		() => [
			{
				color: PRIMARY_COLORS.YELLOW.toLowerCase(),
				name: formatMessage(sharedI18n.roughDraft),
				id: null,
			},
			{
				color: PRIMARY_COLORS.BLUE.toLowerCase(),
				name: formatMessage(sharedI18n.inProgress),
				id: null,
			},
			{
				color: PRIMARY_COLORS.GREEN.toLowerCase(),
				name: formatMessage(sharedI18n.readyForReview),
				id: null,
			},
			{
				color: '#1d7afc',
				name: formatMessage(sharedI18n.verified),
				id: null,
				restrictionLevel: 'PAGE_OWNER',
			},
		],
		[formatMessage],
	);

	const [{ mode, suggestedEnabled, customEnabled, suggestedStatesArray }, setSettingsState] =
		useState<SettingsState>({
			mode: 'disabled',
			suggestedEnabled: true,
			customEnabled: true,
			suggestedStatesArray: originalSuggestedStatesArray,
		});

	// Holds the mode the admin is attempting to switch to from `approvals`. While
	// this is non-null the warning modal is open and `handleModeChange` has NOT
	// been called yet — so the underlying Select value (bound to `mode`) still
	// shows `approvals` and can be cancelled with no mutation fired.
	const [pendingMode, setPendingMode] = useState<'statuses' | 'disabled' | null>(null);

	const {
		data: settingsData,
		loading: settingsLoading,
		error: settingsError,
	} = useQueryInterop<
		StatusAndApprovalsConfigSettingsQuery$data,
		StatusAndApprovalsConfigSettingsQuery$variables
	>(settingsQuery, {
		fetchPolicy: 'cache-and-network',
		variables: { spaceKey },
	});

	// TODO: add more robust error handling for the approval statuses query (e.g. inline error state)
	const { data: approvalStatusesData } = useQueryInterop<
		StatusAndApprovalsConfigApprovalStatusesQuery$data,
		StatusAndApprovalsConfigApprovalStatusesQuery$variables
	>(approvalStatusesQuery, {
		fetchPolicy: 'cache-and-network',
		variables: { cloudId },
		skip: mode !== 'approvals',
		onError: (error: Error) => showErrorFlag(error),
	});

	const [setContentStateSettings] = useMutationInterop<
		StatusAndApprovalsConfigSetContentStateSettingsMutation$data,
		StatusAndApprovalsConfigSetContentStateSettingsMutation$variables
	>(setContentStateMutation);

	const [setApprovalsSettings] = useMutationInterop<
		StatusAndApprovalsConfigSetApprovalsMutation$data,
		StatusAndApprovalsConfigSetApprovalsMutation$variables
	>(setApprovalsMutation);

	const setSuggestedStatesArray = (newStates: Array<ContentState | null>) =>
		setSettingsState((prev) => ({ ...prev, suggestedStatesArray: newStates }));

	// Derive all settings state from query data in a single update
	useEffect(() => {
		if (settingsData) {
			const contentStateData = settingsData?.space?.settings?.contentStateSettings;
			const contentStatesAllowed = contentStateData?.contentStatesAllowed ?? false;
			const isContentApprovalsEnabled =
				settingsData?.space?.spaceTypeSettings?.enabledFeatures?.isContentApprovalsEnabled ?? false;

			let newMode: Mode;
			if (!contentStatesAllowed) {
				newMode = 'disabled';
			} else if (isContentApprovalsEnabled) {
				newMode = 'approvals';
			} else {
				newMode = 'statuses';
			}

			const newStates = [
				...(contentStateData?.spaceContentStates ?? originalSuggestedStatesArray),
			].map((s): ContentState | null => s ?? null);

			setSettingsState({
				mode: newMode,
				suggestedEnabled: contentStateData?.spaceContentStatesAllowed ?? true,
				customEnabled: contentStateData?.customContentStatesAllowed ?? true,
				suggestedStatesArray: newStates,
			});
		}
	}, [settingsData, originalSuggestedStatesArray]);

	// Derive showRestoreDefaults without a separate state to avoid chain updates
	const showRestoreDefaults = useMemo(() => {
		if (suggestedStatesArray.length !== originalSuggestedStatesArray.length) {
			return true;
		}
		for (let i = 0; i < suggestedStatesArray.length; ++i) {
			if (
				suggestedStatesArray[i]?.color?.toUpperCase() !==
					originalSuggestedStatesArray[i].color?.toUpperCase() ||
				suggestedStatesArray[i]?.name !== originalSuggestedStatesArray[i].name
			) {
				return true;
			}
		}
		return false;
	}, [suggestedStatesArray, originalSuggestedStatesArray]);

	const showErrorFlagForError = (error: unknown) => {
		showErrorFlag(error instanceof Error ? error : new Error(String(error)));
	};

	// TODO: add more robust error handling with state rollback on mutation failure
	const handleModeChange = async (newMode: Mode) => {
		setSettingsState((prev) => ({ ...prev, mode: newMode }));
		const contentStatesEnabled = newMode !== 'disabled';
		const approvalsEnabled = newMode === 'approvals';

		try {
			await setContentStateSettings({
				variables: {
					spaceKey,
					contentStatesSetting: contentStatesEnabled,
					spaceStatesSetting: contentStatesEnabled ? suggestedEnabled : true,
					customStatesSetting: contentStatesEnabled ? customEnabled : true,
				},
			});
		} catch (error) {
			showErrorFlagForError(error);
		}

		try {
			await setApprovalsSettings({
				variables: {
					cloudId,
					spaceKey,
					enabled: approvalsEnabled,
				},
			});
		} catch (error) {
			showErrorFlagForError(error);
		}
	};

	// TODO: add more robust error handling with state rollback on mutation failure
	const onSuggestedToggleChange = () => {
		const newSuggestedEnabled = !suggestedEnabled;
		setSettingsState((prev) => ({ ...prev, suggestedEnabled: newSuggestedEnabled }));
		setContentStateSettings({
			variables: {
				spaceKey,
				contentStatesSetting: true,
				spaceStatesSetting: newSuggestedEnabled,
				customStatesSetting: customEnabled,
			},
		}).catch(showErrorFlagForError);
	};

	// TODO: add more robust error handling with state rollback on mutation failure
	const onCustomToggleChange = () => {
		const newCustomEnabled = !customEnabled;
		setSettingsState((prev) => ({ ...prev, customEnabled: newCustomEnabled }));
		setContentStateSettings({
			variables: {
				spaceKey,
				contentStatesSetting: true,
				spaceStatesSetting: suggestedEnabled,
				customStatesSetting: newCustomEnabled,
			},
		}).catch(showErrorFlagForError);
	};
	// Build additionalRows from workflow statuses for approvals mode
	const workflowAdditionalRows = useMemo<AdditionalRow[]>(() => {
		const approvalStatuses = approvalStatusesData?.confluence_contentApprovalStatuses;
		if (!approvalStatuses?.length) {
			// eslint-disable-line confluence-feature-gating/no-preconditioning
			return [];
		}
		const rows: AdditionalRow[] = [
			{
				key: 'start-approval',
				insertBeforeRestrictedState: true,
				node: (
					<SuggestedContentState
						spaceKey={spaceKey}
						color={PRIMARY_COLORS.BLUE}
						name={formatMessage(i18n.startApproval)}
						removeState={noop}
						hideTrash
						icon={StatusWorkflowInReviewIcon}
						iconColor={token('color.icon.accent.blue')}
						showBetaTag
					/>
				),
			},
		];
		for (const s of approvalStatuses) {
			if (s != null) {
				let icon;
				let iconColor;
				if (s.id === CHANGES_REQUESTED_STATUS_ID) {
					icon = StatusWorkflowReturnedIcon;
					iconColor = token('color.icon.accent.blue');
				} else if (s.id === IN_REVIEW_STATUS_ID) {
					icon = StatusWorkflowInReviewIcon;
					iconColor = token('color.icon.accent.blue');
				} else {
					icon = CheckCircleIcon;
					iconColor = token('color.icon.accent.lime');
				}
				rows.push({
					key: s.id ?? s.displayProperties?.name ?? 'workflow',
					insertBeforeRestrictedState: true,
					node: (
						<SuggestedContentState
							spaceKey={spaceKey}
							color={PRIMARY_COLORS.BLUE}
							name={s.displayProperties?.name ?? null}
							removeState={noop}
							hideTrash
							icon={icon}
							iconColor={iconColor}
						/>
					),
				});
			}
		}
		return rows;
	}, [approvalStatusesData, formatMessage, spaceKey]);

	const selectOptions = [
		{ value: 'disabled' as Mode, label: formatMessage(i18n.disabled) },
		{ value: 'statuses' as Mode, label: formatMessage(i18n.statusesOnly) },
		{ value: 'approvals' as Mode, label: formatMessage(i18n.statusesWithApprovals) },
	];

	const selectedOption = selectOptions.find((o) => o.value === mode) ?? selectOptions[0];

	if (settingsError) {
		markErrorAsHandled(settingsError);
		return <ErrorDisplay error={settingsError} />;
	}

	if (settingsLoading) {
		return (
			<Box xcss={spinnerStyles.root}>
				<Spinner />
			</Box>
		);
	}

	return (
		<>
			<Box xcss={selectWrapperStyles.root}>
				<Select
					inputId="status-and-approvals-mode-select"
					testId="status-and-approvals-mode-select"
					options={selectOptions}
					value={selectedOption}
					onChange={(option: { value: Mode; label: string } | null) => {
						if (!option) {
							return;
						}
						// When leaving `approvals` mode for `statuses` or `disabled`, defer the
						// mutation until the admin confirms in the warning modal. Cancelling the
						// modal leaves `mode` untouched, so the Select naturally rolls back.
						if (mode === 'approvals' && option.value !== 'approvals') {
							setPendingMode(option.value);
							return;
						}
						void handleModeChange(option.value);
					}}
				/>
			</Box>
			{pendingMode !== null && (
				<DisableApprovalsModal
					targetMode={pendingMode}
					onConfirm={() => {
						const targetMode = pendingMode;
						setPendingMode(null);
						void handleModeChange(targetMode);
					}}
					onClose={() => setPendingMode(null)}
				/>
			)}
			{mode === 'statuses' && (
				<Box xcss={statusesSectionStyles.root}>
					<ToggleSection
						testId="suggested-states-toggle"
						spaceKey={spaceKey}
						titleText={formatMessage(sharedI18n.suggestedStateSuggestionsTitle)}
						bodyText={formatMessage(sharedI18n.suggestedStateSuggestionsBody)}
						isChecked={suggestedEnabled}
						underlinedBodyText={formatMessage(sharedI18n.restoreDefaults)}
						originalSuggestedStatesArray={originalSuggestedStatesArray}
						setSuggestedStatesArray={setSuggestedStatesArray}
						onToggleClick={onSuggestedToggleChange}
						showRestoreDefaults={showRestoreDefaults}
					/>
					<SuggestedContentStateCustomizationArea
						spaceKey={spaceKey}
						showSuggested={suggestedEnabled}
						suggestedStatesArray={suggestedStatesArray}
						setSuggestedStatesArray={setSuggestedStatesArray}
					/>
					<ToggleSection
						testId="custom-states-toggle"
						spaceKey={spaceKey}
						titleText={formatMessage(sharedI18n.allowCustomContentStatesTitle)}
						bodyText={formatMessage(sharedI18n.allowCustomContentStatesBody)}
						isChecked={customEnabled}
						onToggleClick={onCustomToggleChange}
					/>
				</Box>
			)}
			{mode === 'approvals' && (
				<Box xcss={statusesSectionStyles.root}>
					<ToggleSection
						testId="suggested-states-toggle"
						spaceKey={spaceKey}
						titleText={formatMessage(sharedI18n.suggestedStateSuggestionsTitle)}
						bodyText={formatMessage(sharedI18n.suggestedStateSuggestionsBody)}
						underlinedBodyText={formatMessage(sharedI18n.restoreDefaults)}
						originalSuggestedStatesArray={originalSuggestedStatesArray}
						setSuggestedStatesArray={setSuggestedStatesArray}
						showRestoreDefaults={showRestoreDefaults}
						isChecked
						isToggleDisabled
						disabledTooltip={formatMessage(i18n.suggestedStatesRequiredForApprovals)}
						onToggleClick={noop}
					/>
					<SuggestedContentStateCustomizationArea
						spaceKey={spaceKey}
						showSuggested
						suggestedStatesArray={suggestedStatesArray}
						setSuggestedStatesArray={setSuggestedStatesArray}
						additionalRows={workflowAdditionalRows}
					/>
					<ToggleSection
						testId="custom-states-toggle"
						spaceKey={spaceKey}
						titleText={formatMessage(sharedI18n.allowCustomContentStatesTitle)}
						bodyText={formatMessage(sharedI18n.allowCustomContentStatesBody)}
						isChecked={customEnabled}
						onToggleClick={onCustomToggleChange}
					/>
				</Box>
			)}
		</>
	);
};

export const StatusAndApprovalsConfig = withFlags(StatusAndApprovalsConfigComponent);
